import { ModelBase } from "../ModelBase.js";
import { IrrigationSetAPI } from "../../APICommands/IrrigationSetAPI.js";
import { IrrigationSetJSON } from "../../APICommands/APITypes.js";
import { MasterIndex } from "../AMasterIndex.js";
import { Zone } from "../Zone.js";

export class IrrigationSet extends ModelBase {
    defaultZoneWateringPercentages: Record<string, number>;
    defaultIrrigationDuration: number;
    pausedZoneIds: string[];
    zoneIds: string[];
    zones: Zone[];
    interChunkGap: number;
    chunkByZoneID: Record<string, number>;
    _lastJSON: IrrigationSetJSON;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    zoneSortFunction: (a: any, b: any) => number;

    /**
     *
     * @param name
     * @param connectedZones
     * @param defaultZoneWateringPercentages
     * @param defaultZoneIrrigationOffsets
     * @param defaultIrrigationDuration
     * @param options
     * @returns {Promise<IrrigationSet>}
     * @constructor
     */
    static async createNew(
        name: string,
        connectedZones: string[],
        defaultZoneWateringPercentages: Record<string, number>,
        defaultZoneIrrigationOffsets: Record<string, number>,
        defaultIrrigationDuration: number,
        options: Partial<IrrigationSetJSON>,
    ) {
        const result = await IrrigationSetAPI.addOne(
            name,
            connectedZones,
            defaultZoneWateringPercentages,
            defaultZoneIrrigationOffsets,
            defaultIrrigationDuration,
            options,
        );
        return new IrrigationSet(result);
    }
    loadFromJSON(json) {
        this._lastJSON = json;
        this.dbID = json._id;
        this.name = json.name;
        this.defaultIrrigationDuration =
            json.defaultIrrigationDuration ?? this.defaultIrrigationDuration ?? 1000 * 60 * 60;

        this.defaultZoneWateringPercentages = Object.assign({}, json.defaultZoneWateringPercentages ?? {});
        this.chunkByZoneID = Object.assign({}, json.chunkByZoneID ?? this.chunkByZoneID ?? {});
        this.pausedZoneIds = json.pausedZones;

        this.zoneIds = [...(json.zones ?? [])];
        this.interChunkGap = json.interChunkGap ?? 1000 * 60 * 10;
        // let dataPerZoneID = this.zoneIds.map((zid) => {
        //     return {
        //         wateringPercentage: this.defaultZoneWateringPercentages[zid],
        //         durationOffset: this.defaultZoneIrrigationOffsets[zid],
        //         paused: this.pausedZoneIds.includes(zid),
        //         chunk: this.chunkByZoneID[zid] || 1,
        //     };
        // });
        //smallest to largets
        // dataPerZoneID = dataPerZoneID.sort((a, b) => {
        //     return a.durationOffset - b.durationOffset;
        // });
    }
    getJSON() {
        return {
            name: this.name,
            defaultIrrigationDuration: this.defaultIrrigationDuration,
            defaultZoneWateringPercentages: this.defaultZoneWateringPercentages,
            chunkByZoneID: this.chunkByZoneID,
            pausedZoneIds: this.pausedZoneIds || [],
            zones: this.zoneIds,
            interChunkGap: this.interChunkGap,
        };
    }

    revertChanges() {
        this.loadFromJSON(this._lastJSON);
        this.loadZones();
    }

    constructor(json: Partial<IrrigationSetJSON>) {
        super(json);
        this.category = "irrigationSet";
        this.loadFromJSON(json);
        this.load().catch((e) => {
            console.warn(e);
        });
        this.zoneSortFunction = (a, b) => {
            let nameA = "";
            let nameB = "";
            if (a instanceof ModelBase) {
                nameA = a.name;
            } else if (typeof a === "string" || a instanceof String) {
                // @ts-ignore
                nameA = a;
            }
            if (b instanceof ModelBase) {
                nameB = b.name;
            } else if (typeof b === "string" || b instanceof String) {
                // @ts-ignore
                nameB = b;
            }
            if ((this.chunkByZoneID[a.id] || 1) > (this.chunkByZoneID[b.id] || 1)) {
                return 1;
            } else if ((this.chunkByZoneID[a.id] || 1) < (this.chunkByZoneID[b.id] || 1)) {
                return -1;
            } else {
                return nameA.localeCompare(nameB, "en", { numeric: true });
            }
        };
    }
    async loadZones() {
        const partialZoneList = [];
        const promises = [];
        this.zoneIds.forEach((zid, i) => {
            if (MasterIndex.zone.byID[zid]) {
                partialZoneList[i] = MasterIndex.zone.byID[zid];
            } else {
                promises.push(
                    MasterIndex.zone.byIDeventually(zid).then((r) => {
                        partialZoneList[i] = r;
                    }),
                );
            }
        });
        while (promises.length > 0) {
            await promises.pop();
        }
        this.zones = partialZoneList;
    }
    async load() {
        await this.loadZones();
        await super.load();
    }

    // @ts-ignore
    async edit(changes: Partial<IrrigationSetJSON>) {
        const result = await IrrigationSetAPI.editOne(this.id, changes);
        this.loadFromJSON(result);
        await this.loadZones();
        await super.edit();
        return result;
    }
    generateDefaultDurationGivenZoneEndTime(
        endTimeRelativeToStart,
        zoneID,
        wateringPercentages?: Record<string, number>,
    ) {
        let sumOfTIPs = 0;
        let numOfGaps = 0;
        const lastChunk = Math.max(...Object.values(this.chunkByZoneID));
        if (!wateringPercentages) {
            wateringPercentages = this.defaultZoneWateringPercentages;
        }
        for (let i = 0; i <= lastChunk; i++) {
            const zonesInChunk = Object.keys(wateringPercentages).filter((zid) => this.chunkByZoneID[zid] === i);
            let chunkDuration = 0;
            const highestIrrigationDuration = Math.max(0, 0, ...zonesInChunk.map((zid) => wateringPercentages[zid]));

            chunkDuration = highestIrrigationDuration;

            if (zoneID && zonesInChunk.includes(zoneID)) {
                sumOfTIPs += wateringPercentages[zoneID];
                const newDuration = (endTimeRelativeToStart - numOfGaps * this.interChunkGap) / sumOfTIPs;
                return {
                    newDuration: newDuration,
                    newZoneStartTime:
                        newDuration * (sumOfTIPs - wateringPercentages[zoneID]) + numOfGaps * this.interChunkGap,
                };
            }
            //If chunk duration is zero, it's a placeholder chunk that doesn't need to be counted.
            if (chunkDuration > 0) {
                numOfGaps += 1;
            }
            sumOfTIPs += highestIrrigationDuration;
        }
        if (!zoneID) {
            // `numOfGaps` was incremented once per non-empty chunk, so it equals the chunk count N.
            // Gaps sit *between* chunks, so the actual count is N-1 (clamped at 0 for the 0/1-chunk case).
            const interChunkGaps = Math.max(0, numOfGaps - 1);
            const newDuration = (endTimeRelativeToStart - interChunkGaps * this.interChunkGap) / sumOfTIPs;
            return {
                newDuration: newDuration,
                newZoneStartTime: 0,
            };
        }
    }

    generateIrrigationOffsets(
        defaultDuaration: number,
        wateringPercentages?: Record<string, number>,
        chunkOverride?: Record<string, number>,
    ): Record<string, number> {
        const result = {};
        if (!wateringPercentages) {
            wateringPercentages = this.defaultZoneWateringPercentages;
        }
        let chunksByZoneID = { ...this.chunkByZoneID };
        if (chunkOverride) {
            chunksByZoneID = Object.assign(chunksByZoneID, chunkOverride);
        }

        const chunkStartTimes = [0];
        let curChunkEndTime = 0;

        const lastChunk = Math.max(...Object.values(chunksByZoneID));

        for (let i = 0; i <= lastChunk; i++) {
            const zonesInChunk = Object.keys(wateringPercentages).filter((zid) => chunksByZoneID[zid] === i);

            let chunkDuration = 0;
            const highestIrrigationDuration =
                Math.max(0, 0, ...zonesInChunk.map((zid) => wateringPercentages[zid])) * defaultDuaration;

            chunkDuration = highestIrrigationDuration;
            //If chunk duration is zero, it's a placeholder chunk that doesn't need to be counted.

            if (chunkDuration > 0) {
                chunkDuration = chunkDuration + this.interChunkGap;
            }

            const curChunkStartTime = curChunkEndTime;
            curChunkEndTime += chunkDuration;
            chunkStartTimes.push(curChunkStartTime);

            zonesInChunk.forEach((zid) => {
                result[zid] = curChunkStartTime;
            });
        }

        return result;
    }

    async editSettings(newSettings: Partial<IrrigationSetJSON>) {
        await this.edit(newSettings);
        const promises = [];
        MasterIndex.repeatingEvent.all.forEach((repeatingEvent) => {
            if (repeatingEvent.linkedIrrigationSetId === this.id) {
                promises.push(
                    repeatingEvent.edit(
                        this.defaultZoneWateringPercentages,
                        undefined,
                        undefined,
                        undefined,
                        this.generateIrrigationOffsets(
                            repeatingEvent.defaultDuration,
                            this.defaultZoneWateringPercentages,
                        ),
                    ),
                );
            }
        });
        while (promises.length > 0) {
            await promises.pop();
        }
    }

    async delete() {
        await IrrigationSetAPI.deleteOne(this.id);
        return super.delete();
    }
}
